import { Component, OnInit, TemplateRef } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { AjaxService } from 'src/app/service/ajax.service';
import { UtilService } from 'src/app/service/util.service';
import { API } from 'src/environments/environment';

interface PlanoContaItem {
  id: number;
  codigo: string;
  nome: string;
  tipo: 'SINTETICA' | 'ANALITICA';
  natureza: 'ENTRADA' | 'SAIDA';
  contaPaiId: number | null;
  ativo: boolean;
  dataCriacao?: string;
}

interface PlanoContaArvoreItem {
  id: number;
  codigo: string;
  nome: string;
  tipo: string;
  natureza: string;
  contaPaiId?: number | null;
  ativo?: boolean;
  filhos: PlanoContaArvoreItem[];
  level?: number;
}

@Component({
  selector: 'app-plano-contas',
  templateUrl: './plano-contas.component.html',
  styleUrls: ['./plano-contas.component.scss']
})
export class PlanoContasComponent implements OnInit {
  itens: PlanoContaItem[] = [];
  itensArvore: PlanoContaArvoreItem[] = [];
  itensArvoreFlat: PlanoContaArvoreItem[] = [];
  nosExpandidos: { [id: number]: boolean } = {};

  modalRef: NgbModalRef | null = null;
  editandoId = 0;
  codigo = '';
  nome = '';
  tipo: 'SINTETICA' | 'ANALITICA' = 'ANALITICA';
  natureza: 'ENTRADA' | 'SAIDA' = 'ENTRADA';
  contaPaiId: number | null = null;
  ativo = true;

  constructor(
    private ajaxService: AjaxService,
    private util: UtilService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.carregar();
    this.carregarArvore();
  }

  get contasPaiDisponiveis(): PlanoContaItem[] {
    return this.itens
      .filter((x) => this.isTipoSintetica(x.tipo) && x.id !== this.editandoId)
      .sort((a, b) => a.codigo.localeCompare(b.codigo));
  }

  carregar(): void {
    this.util.showLoading();
    this.carregarItensPlanoContas(
      () => this.util.closeLoading(),
      () => {
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Não foi possível carregar o plano de contas.');
      }
    );
  }

  carregarArvore(): void {
    this.ajaxService.get(`${API}financeiro/planocontas/arvore`).subscribe({
      next: (response) => {
        const data = this.extrairLista(response);
        this.itensArvore = data as PlanoContaArvoreItem[];
        this.itensArvoreFlat = [];
        this.flattenArvore(this.itensArvore, 0);
        this.definirExpansaoPadrao(this.itensArvore);
      }
    });
  }

  novo(modal: TemplateRef<any>): void {
    this.editandoId = 0;
    this.codigo = '';
    this.nome = '';
    this.tipo = 'ANALITICA';
    this.natureza = 'ENTRADA';
    this.contaPaiId = null;
    this.ativo = true;

    // Sempre recarrega antes de abrir o modal para atualizar Conta Pai
    this.util.showLoading();
    this.carregarItensPlanoContas(
      () => {
        this.util.closeLoading();
        this.modalRef = this.modalService.open(modal, { size: 'lg', backdrop: 'static' });
      },
      () => {
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Não foi possível atualizar as contas pai.');
      }
    );
  }

  editar(item: PlanoContaItem, modal: TemplateRef<any>): void {
    this.editandoId = item.id;
    this.codigo = item.codigo;
    this.nome = item.nome;
    this.tipo = item.tipo;
    this.natureza = item.natureza;
    this.contaPaiId = item.contaPaiId;
    this.ativo = item.ativo;
    this.modalRef = this.modalService.open(modal, { size: 'lg', backdrop: 'static' });
  }

  novoFilha(itemArvore: PlanoContaArvoreItem, modal: TemplateRef<any>): void {
    this.util.showLoading();
    this.carregarItensPlanoContas(
      () => {
        this.util.closeLoading();
        this.editandoId = 0;
        this.codigo = '';
        this.nome = '';
        this.tipo = 'ANALITICA';
        this.natureza = this.normalizarNatureza(itemArvore.natureza);
        this.contaPaiId = Number(itemArvore.id);
        this.ativo = true;
        this.modalRef = this.modalService.open(modal, { size: 'lg', backdrop: 'static' });
      },
      () => {
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Não foi possível atualizar os dados da árvore.');
      }
    );
  }

  editarArvore(itemArvore: PlanoContaArvoreItem, modal: TemplateRef<any>): void {
    this.util.showLoading();
    this.carregarItensPlanoContas(
      () => {
        this.util.closeLoading();
        const item = this.itens.find((x) => x.id === Number(itemArvore.id));
        if (!item) {
          this.util.toasterShowError('Erro', 'Conta não encontrada para edição.');
          return;
        }
        this.editar(item, modal);
      },
      () => {
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Não foi possível carregar a conta para edição.');
      }
    );
  }

  excluirArvore(itemArvore: PlanoContaArvoreItem): void {
    const item = this.itens.find((x) => x.id === Number(itemArvore.id));
    if (!item) {
      this.util.toasterShowError('Erro', 'Conta não encontrada para exclusão.');
      return;
    }
    this.excluir(item);
  }

  temFilhos(itemArvore: PlanoContaArvoreItem): boolean {
    return !!itemArvore?.filhos?.length;
  }

  estaExpandido(itemArvore: PlanoContaArvoreItem): boolean {
    return this.nosExpandidos[itemArvore.id] !== false;
  }

  alternarExpandir(itemArvore: PlanoContaArvoreItem): void {
    this.nosExpandidos[itemArvore.id] = !this.estaExpandido(itemArvore);
  }

  podeAdicionarFilha(itemArvore: PlanoContaArvoreItem): boolean {
    return this.isTipoSintetica(itemArvore.tipo);
  }

  salvar(): void {
    if (!this.codigo.trim()) {
      this.util.toasterShowError('Atenção', 'Código é obrigatório.');
      return;
    }
    if (!this.nome.trim()) {
      this.util.toasterShowError('Atenção', 'Nome é obrigatório.');
      return;
    }
    if (this.contaPaiId && this.contaPaiId === this.editandoId) {
      this.util.toasterShowError('Atenção', 'A conta não pode ser pai dela mesma.');
      return;
    }

    const payload = {
      codigo: this.codigo.trim(),
      Codigo: this.codigo.trim(),
      nome: this.nome.trim(),
      Nome: this.nome.trim(),
      tipo: this.tipo,
      Tipo: this.tipo,
      natureza: this.natureza,
      Natureza: this.natureza,
      contaPaiId: this.contaPaiId,
      ContaPaiId: this.contaPaiId,
      ativo: this.ativo
    };

    this.util.showLoading();
    const request = this.editandoId > 0
      ? this.ajaxService.put(`${API}financeiro/planocontas/${this.editandoId}`, payload)
      : this.ajaxService.post(`${API}financeiro/planocontas`, payload);

    request.subscribe({
      next: (response) => {
        this.util.closeLoading();
        if (response?.hasErrors) {
          this.util.toasterShowError('Erro', response.message || 'Não foi possível salvar.');
          return;
        }
        this.util.toasterShowSuccess('Sucesso', this.editandoId > 0 ? 'Conta atualizada com sucesso!' : 'Conta cadastrada com sucesso!');
        this.modalRef?.close();
        this.carregar();
        this.carregarArvore();
      },
      error: () => {
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Erro ao salvar conta.');
      }
    });
  }

  excluir(item: PlanoContaItem): void {
    this.util.showLoading();
    this.ajaxService.delete(`${API}financeiro/planocontas/${item.id}`).subscribe({
      next: (response) => {
        this.util.closeLoading();
        if (response?.hasErrors) {
          this.util.toasterShowError('Erro', response.message || 'Não foi possível excluir.');
          return;
        }
        this.util.toasterShowSuccess('Sucesso', 'Conta excluída com sucesso!');
        this.carregar();
        this.carregarArvore();
      },
      error: () => {
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Erro ao excluir conta.');
      }
    });
  }

  private flattenArvore(lista: PlanoContaArvoreItem[], level: number): void {
    (lista || []).forEach((item) => {
      const clone: PlanoContaArvoreItem = { ...item, level };
      this.itensArvoreFlat.push(clone);
      if (item.filhos?.length) {
        this.flattenArvore(item.filhos, level + 1);
      }
    });
  }

  private definirExpansaoPadrao(lista: PlanoContaArvoreItem[]): void {
    (lista || []).forEach((item) => {
      this.nosExpandidos[item.id] = true;
      if (item.filhos?.length) {
        this.definirExpansaoPadrao(item.filhos);
      }
    });
  }

  private carregarItensPlanoContas(onSuccess?: () => void, onError?: () => void): void {
    this.ajaxService.get(`${API}financeiro/planocontas`).subscribe({
      next: (response) => {
        const data = this.extrairLista(response);
        this.itens = data.map((x: any) => {
          const item = this.util.converterParaLowercase(x || {});
          const tipoNormalizado = this.normalizarTipo(item.tipo);
          const naturezaNormalizada = this.normalizarNatureza(item.natureza);
          return {
            id: Number(item.id || 0),
            codigo: String(item.codigo || ''),
            nome: String(item.nome || ''),
            tipo: tipoNormalizado,
            natureza: naturezaNormalizada,
            contaPaiId: item.contapaiid != null ? Number(item.contapaiid) : null,
            ativo: Boolean(item.ativo),
            dataCriacao: String(item.datacriacao || '')
          };
        }).filter((x: PlanoContaItem) => x.id > 0);

        if (onSuccess) onSuccess();
      },
      error: () => {
        if (onError) onError();
      }
    });
  }

  private normalizarTipo(valor: any): 'SINTETICA' | 'ANALITICA' {
    const texto = this.normalizarTexto(String(valor || ''));
    if (texto === 'SINTETICA' || texto === 'S') return 'SINTETICA';
    if (texto === 'ANALITICA' || texto === 'A') return 'ANALITICA';
    return 'ANALITICA';
  }

  private normalizarNatureza(valor: any): 'ENTRADA' | 'SAIDA' {
    const texto = this.normalizarTexto(String(valor || ''));
    if (texto === 'ENTRADA' || texto === 'E') return 'ENTRADA';
    if (texto === 'SAIDA' || texto === 'S') return 'SAIDA';
    return 'ENTRADA';
  }

  private isTipoSintetica(tipo: any): boolean {
    return this.normalizarTipo(tipo) === 'SINTETICA';
  }

  private normalizarTexto(valor: string): string {
    return (valor || '')
      .trim()
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  private extrairLista(response: any): any[] {
    let atual = response;
    for (let i = 0; i < 3; i++) {
      if (Array.isArray(atual)) break;
      if (atual && typeof atual === 'object' && Object.prototype.hasOwnProperty.call(atual, 'data') && atual.data != null) {
        atual = atual.data;
      } else {
        break;
      }
    }
    if (Array.isArray(atual)) return atual;
    if (atual && typeof atual === 'object') return Object.values(atual).filter((x: any) => x && typeof x === 'object');
    return [];
  }
}
